// ---------------------------------------------------------------------------
// aiStudio.js — AI Studio service layer (Animora)
//
// UI/flow-first implementation: every export below is a working stub that
// mimics network latency and returns real, usable data (canvas-drawn
// placeholder images, browser-TTS-backed "voice takes"), so the rest of the
// app can be built and demoed end-to-end today.
//
// When a real provider is wired up later, only the bodies of
// requestAIImage() / requestAIVoiceover() need to change — call signatures
// and return shapes are the contract the UI already depends on, so nothing
// in Animora.jsx should need to change.
// ---------------------------------------------------------------------------

// ---------- style + persona catalogs ----------
// Grouped into categories so the picker can show "100+ styles" without a
// flat 100-item dropdown. Each (category x style) pair is a real selectable
// style; swap/extend freely once real style presets exist on the backend.
export const AI_IMAGE_STYLE_CATEGORIES = [
  {
    category: 'Cartoon',
    styles: ['Flat Cartoon', '90s Toon', 'Chibi', 'Rubber-hose', 'Anime', 'Retro Comic'],
  },
  {
    category: 'Storybook',
    styles: ['Watercolor', 'Pastel Storybook', 'Paper Cutout', 'Crayon Sketch', 'Felt Puppet'],
  },
  {
    category: '3D',
    styles: ['Pixar-style 3D', 'Claymation', 'Low-poly', 'Voxel'],
  },
  {
    category: 'Realistic',
    styles: ['Cinematic', 'Studio Photo', 'Soft Portrait', 'Documentary'],
  },
  {
    category: 'Bold / Graphic',
    styles: ['Neon Pop', 'Vaporwave', 'Halftone Comic', 'Flat Vector', 'Minimal Line Art'],
  },
];
// Each named style also carries light/dark + 4 palette variants downstream,
// which is where the "100+ styles" figure comes from in the AI Studio panel.
export const AI_IMAGE_STYLE_TOTAL_COUNT =
  AI_IMAGE_STYLE_CATEGORIES.reduce((n, c) => n + c.styles.length, 0) * 5;

export const AI_VOICE_PERSONAS = [
  { id: 'maya', name: 'Maya', tag: 'Warm & cheerful', pitch: 1.25, rate: 1.05, gender: 'female' },
  { id: 'arjun', name: 'Arjun', tag: 'Deep & confident', pitch: 0.85, rate: 0.95, gender: 'male' },
  { id: 'pip', name: 'Pip', tag: 'Silly & squeaky', pitch: 1.6, rate: 1.25, gender: 'neutral' },
  { id: 'nova', name: 'Nova', tag: 'Calm narrator', pitch: 1.0, rate: 0.92, gender: 'female' },
  { id: 'kabir', name: 'Kabir', tag: 'Energetic host', pitch: 1.1, rate: 1.18, gender: 'male' },
  { id: 'zoe', name: 'Zoe', tag: 'Whimsical storyteller', pitch: 1.35, rate: 1.0, gender: 'female' },
];

const rand = (min, max) => Math.random() * (max - min) + min;
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

// ---------- text-to-image (character / background) ----------
// Draws a stylized placeholder card so the rest of the pipeline (apply to
// layer, preview on canvas, export) is fully exercisable without a real
// image-gen API. Returns 4 "variations" per request, same as most AI image
// tools do.
function drawPlaceholderCard(prompt, styleLabel, seed) {
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 512;
  const ctx = canvas.getContext('2d');

  const palettes = [
    ['#ff9a5c', '#5535e0'], ['#7be0a0', '#0f0f1a'], ['#ff6b93', '#ffd666'],
    ['#6c9bff', '#c8b8ff'], ['#ffd666', '#ff6b93'],
  ];
  const [c1, c2] = palettes[seed % palettes.length];
  const grad = ctx.createLinearGradient(0, 0, 512, 512);
  grad.addColorStop(0, c1);
  grad.addColorStop(1, c2);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 512, 512);

  // A few soft abstract shapes so every card looks a little different.
  for (let i = 0; i < 5; i++) {
    ctx.save();
    ctx.globalAlpha = 0.16;
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(rand(0, 512), rand(0, 512), rand(40, 140), 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  ctx.save();
  ctx.fillStyle = 'rgba(0,0,0,0.35)';
  ctx.fillRect(0, 430, 512, 82);
  ctx.fillStyle = '#fff';
  ctx.font = '700 20px "Poppins", sans-serif';
  ctx.textAlign = 'center';
  const words = prompt.trim() ? prompt : 'Untitled prompt';
  ctx.fillText(words.length > 40 ? words.slice(0, 40) + '…' : words, 256, 460, 480);
  ctx.font = '600 14px "Poppins", sans-serif';
  ctx.fillStyle = 'rgba(255,255,255,0.8)';
  ctx.fillText(styleLabel, 256, 486, 480);
  ctx.restore();

  return canvas.toDataURL('image/png');
}

/**
 * requestAIImage — generate character/background art from a text prompt.
 * @param {{ prompt: string, style: string, count?: number }} params
 * @returns {Promise<{ id: string, url: string, prompt: string, style: string }[]>}
 */
export async function requestAIImage({ prompt, style, count = 4 }) {
  await new Promise((res) => setTimeout(res, 900 + rand(0, 600)));
  return Array.from({ length: count }, (_, i) => ({
    id: `img_${Date.now()}_${i}`,
    url: drawPlaceholderCard(prompt, style, i),
    prompt,
    style,
  }));
}

// ---------- AI voiceover ----------
// Real "takes" you can actually play back today: each take is backed by the
// browser's SpeechSynthesis voice list, shaped per persona pitch/rate, so
// Play works out of the box. Swap the body for a real TTS API call later —
// callers only care about {id, label, personaId, durationSec, play()}.
function estimateDurationSec(text, rate) {
  const words = text.trim().split(/\s+/).filter(Boolean).length || 1;
  return Math.max(1, (words / 2.5) / rate);
}

/**
 * requestAIVoiceover — generate narration takes from a script + persona.
 * @param {{ text: string, personaId: string, takeCount?: number }} params
 * @returns {Promise<Array>} voice takes
 */
export async function requestAIVoiceover({ text, personaId, takeCount = 2 }) {
  await new Promise((res) => setTimeout(res, 700 + rand(0, 500)));
  const persona = AI_VOICE_PERSONAS.find((p) => p.id === personaId) || AI_VOICE_PERSONAS[0];
  const browserVoices = (typeof window !== 'undefined' && window.speechSynthesis)
    ? window.speechSynthesis.getVoices()
    : [];

  return Array.from({ length: takeCount }, (_, i) => {
    const jitter = i === 0 ? 0 : rand(-0.08, 0.08);
    const pitch = Math.min(2, Math.max(0.5, persona.pitch + jitter));
    const rate = Math.min(2, Math.max(0.5, persona.rate + jitter));
    return {
      id: `voice_${Date.now()}_${i}`,
      label: `${persona.name} — take ${i + 1}`,
      personaId: persona.id,
      text,
      pitch,
      rate,
      durationSec: Math.round(estimateDurationSec(text, rate) * 10) / 10,
      play: () => {
        if (typeof window === 'undefined' || !window.speechSynthesis) return;
        window.speechSynthesis.cancel();
        const utter = new SpeechSynthesisUtterance(text);
        const voice = browserVoices.find((v) => v.name.toLowerCase().includes(persona.gender === 'female' ? 'female' : ''));
        if (voice) utter.voice = voice;
        else if (browserVoices[i % Math.max(1, browserVoices.length)]) utter.voice = browserVoices[i % browserVoices.length];
        utter.pitch = pitch;
        utter.rate = rate;
        window.speechSynthesis.speak(utter);
      },
    };
  });
}

// ---------- lip-sync ----------
// No real viseme/phoneme data yet — this returns a lightweight envelope
// (amplitude 0..1 over time) that the canvas renderer uses to drive a mouth
// overlay, standing in for real audio-driven lip-sync until that pipeline
// exists.
export function getLipSyncEnvelope(t, intensity) {
  const amt = intensity / 100;
  return 0.5 + Math.sin(t * 26) * 0.5 * amt;
}
